import { useState, useEffect, useCallback, useRef } from "react";
import FetchHook from "../Api/FetchHooks";

const UseServerTable = ({ baseUrl, onSuccess, defaultPageSize = 10 }) => {
  // v5 DataGrid uses 0-based indexing for pages
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [rowCount, setRowCount] = useState(0);
  const [tableError, setTableError] = useState(null);

  const [loading, response, request] = FetchHook();

  const dataCache = useRef({});
  const totalCountCache = useRef(0);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    setPage(0);
    dataCache.current = {};
  }, [baseUrl]);

  // The main fetch logic
  const fetchData = useCallback(async () => {
    setTableError(null);

    const cacheKey = `page-${page}-size-${pageSize}`;

    if (dataCache.current[cacheKey]) {
      console.log(`Serving Page ${page + 1} from Cache`);

      // // Restore the Total count from cache
      setRowCount(totalCountCache.current);

      // Immediately push cached data to Redux/Component
      if (onSuccessRef.current) {
        onSuccess(dataCache.current[cacheKey]);
      }
      return;
    }

    const apiPage = page + 1;

    // Check if baseUrl already has '?' to decide whether to add '&' or '?'
    const separator = baseUrl.includes("?") ? "&" : "?";

    const props = {
      url: `${baseUrl}${separator}page_number=${apiPage}&page_size=${pageSize}`,
    };

    try {
      const res = await request(props);

      if (res && res.status === 200) {
        // Update total count for pagination
        setRowCount(res.data.Total || 0);
        totalCountCache.current = res.data.Total || 0;
        dataCache.current[cacheKey] = res.data;

        // Pass data back to component (for Redux dispatch or local state)
        if (onSuccess) {
          onSuccess(res.data);
        }
      }
    } catch (error) {
      console.error("Table Fetch Error", error);

      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.response?.data?.message ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setTableError({
        severity: "error",
        message: errorMessage,
      });
    }
  }, [page, pageSize, baseUrl, request]);

  // Trigger fetch when pagination changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page on size change
    dataCache.current = {};
  };

  return {
    // Spread these directly onto TableComponent
    gridProps: {
      page,
      pageSize,
      rowCount,
      loading,
      paginationMode: "server",
      onPageChange,
      onPageSizeChange,
      rowsPerPageOptions: [10, 20, 30, 50, 100],
    },
    error: tableError,
    refresh: fetchData,
    response,
  };
};

export default UseServerTable;
